import enums from '@/plugins/enums'
import { computed } from 'vue'
import { requestSubmissionFlowSteps } from '@/flows/request-submission/steps'

// Functions for report permissions (should be first for role show)
function canDownloadReport(row) {
  return row.can?.download_report
}

function canGenerateReport(row) {
  return row.can?.generate_report
}

export const getSharedActionsButtons = function () {
  const hasBorrowerLoggedInAfterInvite = computed(
    () => new Date(this.row.borrower?.last_login_at) >= this.row?.referral_at
  )
  return [
    {
      label: 'review_report',
      icon: 'mdi-file-document-alert-outline',
      color: 'main',
      show:
        this.isOwner &&
        this.$config.REQUIRE_OWNER_REVIEW_FOR_DVM &&
        !this.row.accepted_by_owner_at &&
        this.row.valuation_type === enums.modules.DVM &&
        this.reportExists('pdf'),
      to: {
        name: 'valuation.request',
        params: { action: requestSubmissionFlowSteps.REPORT },
        query: { valuation_request_ref: this.row.valuation_request_ref },
      },
    },
    {
      label: 'pdf_report',
      color: 'main',
      icon: 'mdi-download',
      show: canDownloadReport(this.row) && this.showReport('pdf'),
      click: () => this.report.download(),
      disable: this.report.isDownloading.value,
      loading: this.report.isDownloading.value,
    },
    {
      label: 'web_report',
      icon: 'mdi-file-document-arrow-right-outline',
      color: 'main',
      show: this.showReport('web'),
      to: {
        name: 'valuation.report',
        query: {
          valuation_request_ref: this.row.valuation_request_ref,
          static: 'true',
        },
      },
    },
    {
      label: this.row.report_ref ? 'regenerate_pdf_report' : 'generate_pdf_report',
      color: 'main',
      icon: this.row.report_ref ? 'mdi-file-document-refresh-outline' : 'mdi-file-document-plus-outline',
      valuationTypes: ['avm', 'dvm', 'ovm'],
      show:
        canGenerateReport(this.row) &&
        ![
          ...(this.$config.BUILDING_TYPES_WITH_MANUAL_OVM_REPORTS ?? []),
          enums.buildingTypes.GARAGE,
        ].includes(this.row.features.f_building_type) &&
        this.hasRole('dispatcher', this.row.valuation_type) &&
        this.row.status === enums.requestStatus.VALUED,
      click: () => this.report.generate(true),
      disable: this.report.isGenerating.value,
      loading: this.report.isGenerating.value,
    },
    {
      label:
        this.appointmentStatus === enums.appointmentStatus.PENDING_CONFIRMATION && this.isAssignedValuer
          ? 'confirm-appointment'
          : 'appointment',
      color: 'main',
      icon:
        this.appointmentStatus === enums.appointmentStatus.PENDING_CONFIRMATION && this.isAssignedValuer
          ? 'mdi-pencil'
          : 'mdi-eye',
      valuationTypes: ['oepc'],
      show:
        (this.isAssignedValuer || this.hasRole('dispatcher', this.row.valuation_type)) &&
        this.row.valuation_type === 'oepc',
      to: {
        name: 'expert.summary',
        query: {
          valuation_request_ref: this.row.valuation_request_ref,
        },
      },
    },
    // FIXME: need to streamline the mess between `edit_request` and `edit`
    {
      label: 'edit_request',
      icon: 'mdi-pencil',
      valuationTypes: ['avm', 'dvm', 'ovm', 'oepc'],
      // TODO: use only can.edit when it covers all conditions
      show:
        (!this.isOwner &&
          !this.row.is_borrower_submitted &&
          // excluding DRAFT to avoid showing two edit buttons
          this.row.status !== enums.requestStatus.DRAFT &&
          this.row.can?.edit) ||
        (this.hasRole('reporter') &&
          !this.hasRole('valuer') &&
          [enums.requestStatus.SUBMITTED, enums.requestStatus.VALUED].includes(this.row.status) &&
          this.row.borrower_status === 'registration_pending'), // <- legacy conditions (check that covered by can.edit)
      to: {
        name: 'valuation.request',
        params: { action: requestSubmissionFlowSteps.SUBMIT },
        query: { valuation_request_ref: this.row.valuation_request_ref },
      },
      color: 'main',
    },
    {
      label: 'edit',
      color: 'main',
      icon: 'mdi-pencil',
      valuationTypes: ['avm', 'dvm', 'ovm'],
      target: this.row.deployment ? '_blank' : null,
      show: (this.isOwner || this.row.is_borrower_submitted) && this.row.status === enums.requestStatus.DRAFT,
      // FIXME: Should be using Router to generate the URL:
      href: this.row.deployment
        ? `https://${this.row.deployment}.rock.estate/valuation/${this.$route.params.lang}/request/extra-info?valuation_request_ref=${this.row.displayRequestRef}`
        : null,
      to: !this.row.deployment
        ? {
            path: this.hasBeenSubmittedOnce ? '/valuation/request/extra-info' : '/valuation/request/edit',
            query: {
              valuation_request_ref: this.row.valuation_request_ref,
            },
          }
        : null,
    },
    {
      label: 'view_request',
      icon: 'mdi-eye',
      valuationTypes: ['avm', 'dvm', 'ovm', 'oepc'],
      show:
        (this.isOwner || (this.isBorrower && this.row.is_borrower_submitted) || this.hasRole('reporter')) &&
        !this.hasRole('valuer') &&
        [enums.requestStatus.SUBMITTED, enums.requestStatus.VALUED].includes(this.row.status) &&
        this.row.borrower_status !== 'registration_pending',
      to: {
        name: 'valuation.request',
        query: { valuation_request_ref: this.row.valuation_request_ref },
      },
    },
    {
      label: 'pdf_flood_report',
      color: 'main',
      icon: 'mdi-download',
      show:
        (this.isOwner ||
          this.hasRole(['reporter', 'dispatcher'], this.row.valuation_type) ||
          this.row.borrower?.is_self ||
          this.isAssignedValuer) &&
        this.showFloodReport(this.row, 'pdf'),
      click: () => {
        return this.floodReport.download()
      },
      disable: this.floodReport.isDownloading.value,
      loading: this.floodReport.isDownloading.value,
    },
    { 
      label: 'web_flood_report',
      icon: 'mdi-file-document-arrow-right-outline',
      color: 'main',
      show:
        (this.isOwner ||
          this.hasRole(['reporter', 'dispatcher'], this.row.valuation_type) ||
          this.row.borrower?.is_self ||
          this.isAssignedValuer) &&
        this.showFloodReport(this.row, 'web'),
      to: {
        name: 'valuation.flood-report',
        query: {
          valuation_request_ref: this.row.valuation_request_ref,
          static: 'true',
        },
      },
    },
    {
      label: this.row.flood_report_ref ? 'regenerate_pdf_flood_report' : 'generate_pdf_flood_report',
      color: 'main',
      icon: this.row.flood_report_ref
        ? 'mdi-file-document-refresh-outline'
        : 'mdi-file-document-plus-outline',
      valuationTypes: ['avm', 'dvm', 'ovm'],
      show: this.hasRole('dispatcher', this.row.valuation_type),
      click: () => this.floodReport.generate(true),
      disable: this.floodReport.isGenerating.value,
      loading: this.floodReport.isGenerating.value,
    },
    {
      label: 'unsubmit_request',
      color: 'main',
      icon: 'mdi-undo',
      valuationType: ['avm', 'dvm', 'ovm'],
      show: this.row.can?.unsubmit_request,
      click: this.unsubmitRequest,
      disable: this.requestMutation.isBusy,
      loading: this.requestMutation.isBusy,
    },
    {
      label: 'goto-borrower-flow',
      icon: 'mdi-eye-arrow-right-outline',
      show: this.hasRole('dispatcher', this.row.valuation_type) && hasBorrowerLoggedInAfterInvite.value,
      to:
        this.row.valuation_type === 'oepc'
          ? {
              name: 'oepc.request',
              params: { action: 'building-type' },
              query: {
                valuation_request_ref: this.row.valuation_request_ref,
              },
            }
          : {
              name: 'valuation.borrower',
              query: {
                valuation_request_ref: this.row.valuation_request_ref,
              },
            },
    },
    {
      label: 'support_tools',
      icon: 'mdi-magnify',
      show:
        (this.hasRole('dispatcher', this.row.valuation_type) ||
          this.hasRole('hotliner') ||
          this.hasRole('support')) &&
        this.$route.name !== 'dispatcher.support',
      to: {
        name: 'dispatcher.support',
        query: {
          valuation_request_ref: this.row.valuation_request_ref,
        },
      },
    },
  ]
}
